import { useEffect, useRef, useState, type ChangeEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useProfile, useUpdateProfile, useUploadAvatar } from '../api/hooks';
import { useAuth } from '../auth/AuthProvider';
import { Loader, ErrorView } from '../components/StateViews';
import { Avatar } from '../components/Avatar';
import { BlueHeader, HeaderBack } from '../components/ScreenShell';
import { CameraIcon } from '../components/icons';
import { backButton, haptics } from '../telegram';

export function EditProfileScreen() {
  const navigate = useNavigate();
  const auth = useAuth();
  const { data, isLoading, isError, error } = useProfile(null);
  const updateProfile = useUpdateProfile();
  const uploadAvatar = useUploadAvatar();
  const fileRef = useRef<HTMLInputElement>(null);

  const [nickname, setNickname] = useState('');
  const [email, setEmail] = useState('');
  const [seeded, setSeeded] = useState(false);
  const [errMsg, setErrMsg] = useState<string | null>(null);

  useEffect(() => {
    const back = () => navigate(-1);
    backButton.show(back);
    return () => backButton.hide(back);
  }, [navigate]);

  useEffect(() => {
    if (data && !seeded) {
      setNickname(data.displayName ?? '');
      setEmail(data.email ?? '');
      setSeeded(true);
    }
  }, [data, seeded]);

  if (isLoading) return <Loader />;
  if (isError || !data) return <ErrorView message={(error as Error)?.message} />;

  const pickPhoto = () => fileRef.current?.click();

  const onFile = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-picking the same file
    if (!file) return;
    setErrMsg(null);
    try {
      const res = await uploadAvatar.mutateAsync(file);
      auth.patchPlayer({ photoUrl: res.photoUrl });
      haptics.success();
    } catch (err) {
      setErrMsg(err instanceof Error ? err.message : 'Не удалось загрузить фото');
      haptics.error();
    }
  };

  const save = async () => {
    setErrMsg(null);
    try {
      const updated = await updateProfile.mutateAsync({ displayName: nickname, email });
      auth.patchPlayer({ displayName: updated.displayName, photoUrl: updated.photoUrl });
      haptics.success();
      navigate(-1);
    } catch (err) {
      setErrMsg(err instanceof Error ? err.message : 'Не удалось сохранить');
      haptics.error();
    }
  };

  return (
    <div>
      <BlueHeader>
        <div className="bhead-top" style={{ marginBottom: 0 }}>
          <HeaderBack onClick={() => navigate(-1)} />
          <div className="bhead-title" style={{ flex: 1, fontSize: 16 }}>
            Изменить профиль
          </div>
          <button className="subhead-action" onClick={save} disabled={updateProfile.isPending}>
            {updateProfile.isPending ? 'Сохраняем…' : 'Готово'}
          </button>
        </div>
      </BlueHeader>

      <div className="content">
      <input
        ref={fileRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        hidden
        onChange={onFile}
      />

      <div className="edit-avatar-wrap">
        <button
          className="edit-avatar"
          onClick={pickPhoto}
          aria-label="Изменить фото"
          disabled={uploadAvatar.isPending}
        >
          <Avatar name={data.displayName} src={data.photoUrl} size={88} bordered />
          <span className="cam">
            <CameraIcon />
          </span>
          {uploadAvatar.isPending && <span className="edit-avatar-loading" aria-hidden="true" />}
        </button>
        <button className="edit-photo-btn" onClick={pickPhoto} disabled={uploadAvatar.isPending}>
          {uploadAvatar.isPending ? 'Загрузка…' : 'Изменить фото'}
        </button>
      </div>

      <div className={`field ${nickname ? 'field--active' : ''}`}>
        <div className="field-label">Никнейм</div>
        <input
          value={nickname}
          onChange={(e) => setNickname(e.target.value)}
          placeholder="Ваш никнейм"
          maxLength={40}
        />
      </div>

      <div className="field">
        <div className="field-label">Email</div>
        <input
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
          type="email"
          inputMode="email"
          maxLength={254}
        />
      </div>

      {errMsg && <div className="field-error">{errMsg}</div>}
      </div>
    </div>
  );
}
